import React, { useEffect, useState } from 'react';
import LinuxDownloadModal from './linux-download-modal';
import * as Icon from 'ubs-icons';

import NoLinks from './no-links';
import { COLOR } from '../common/constant';

const TypeWriter = ({ text, delay = 50 }) => {
    const [currentText, setCurrentText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
        if (currentIndex < text.length) {
            const timeout = setTimeout(() => {
                setCurrentText(prevText => prevText + text[currentIndex]);
                setCurrentIndex(prevIndex => prevIndex + 1);
            }, delay);

            return () => clearTimeout(timeout);
        } else {
            setIsComplete(true);
        }
    }, [currentIndex, delay, text]);

    return (
        <React.Fragment>
            <span>{currentText}</span>
            {!isComplete && <span className="u_typewriter-cursor"></span>}
        </React.Fragment>
    );
};

const DownloadCard = ({ icon: CardIcon, iconFill, title, children }) => (
    <div className="u_col-4 u_pb-20">
        <div className="u_download-modern-card u_b-light">
            <div className="u_download-modern-card-icon">
                <CardIcon size={40} fill={iconFill} />
            </div>
            <div className="u_download-modern-card-title">{title}</div>
            <div className="u_download-modern-card-links">
                {children}
            </div>
        </div>
    </div>
);

const DownloadLinkRow = ({ label, link, app, platform, onClick }) => (
    <a href="javascript:;" onClick={() => onClick(link, app, platform)} className="u_download-modern-card-link">
        <span className="u_download-modern-card-link-text">{label}</span>
        <Icon.Download size={16} fill={COLOR.u_icon_bg} />
    </a>
);

const UDownload = ({ downloadData = {}, onlyTrackApp, user, companyId, sendData }) => {
    const [activeOS, setActiveOS] = useState('Windows');
    const [showLinuxModal, setShowLinuxModal] = useState(false);

    useEffect(() => {
        const handleMouseUp = (e) => {
            const container = document.querySelector('.u_download-version');
            if (container && !container.contains(e.target)) {
                document.querySelectorAll('.u_download-version-list')?.forEach(_ => {
                    _?.classList.add('u_hidden');
                });
            }
        };

        document.addEventListener('mouseup', handleMouseUp);
        return () => document.removeEventListener('mouseup', handleMouseUp);
    }, []);

    useEffect(() => {
        const platform = window.navigator.platform.toLowerCase();
        if (platform.includes('mac')) setActiveOS('Mac');
        else if (platform.includes('linux')) setActiveOS('Linux');
        else setActiveOS('Windows');
    }, []);

    const onDownload = (link, app, platform) => {
        if (platform === 'Linux') {
            setShowLinuxModal(true);
            return;
        }
        window.open(link, '_blank');
        if (platform && app === 'Superworks' && sendData) {
            sendData('track|master|download|app', {
                platform: platform,
            })
        }
    };

    const renderDownloadSection = (title, items) => {
        const platformItems = items?.filter(item => item?.link && item?.link?.length !== 0);
        if (platformItems?.length === 0) return null;

        return (
            <div className="u_download-wpr">
                <div className="u_download-header">
                    <h5>{title}</h5>
                </div>
                <div className="u_download-modern u_row">
                    {Array.isArray(platformItems) && platformItems?.map((item, idx) => (
                        <DownloadCard key={idx} icon={item?.icon} title={item?.title} iconFill={item?.iconFill}>
                            {Array.isArray(item?.versions) && item?.versions?.map((v, vIdx) => (
                                <DownloadLinkRow
                                    key={vIdx}
                                    label={v?.label}
                                    link={v?.link}
                                    app={item?.app}
                                    platform={v?.platform}
                                    onClick={onDownload}
                                />
                            ))}
                        </DownloadCard>
                    ))}
                </div>
            </div>
        );
    };

    const desktopItems = [
        {
            title: 'Windows',
            icon: Icon.WindowsColor,
            app: "Superworks",
            link: downloadData?.trackWindows32?.link || downloadData?.trackWindows64?.link,
            versions: [
                { label: 'Download for Windows 64', link: downloadData?.trackWindows64?.link, platform: 'Windows 64' },
                { label: 'Download for Windows 32', link: downloadData?.trackWindows32?.link, platform: 'Windows 32' }
            ].filter(v => v.link)
        },
        {
            title: 'Mac',
            icon: Icon.Apple,
            iconFill: COLOR.u_black,
            app: "Superworks",
            link: downloadData?.trackMac?.link || downloadData?.trackMacIntel?.link,
            versions: [
                { label: 'Download for Mac Silicon', link: downloadData?.trackMac?.link, platform: 'Mac Silicon' },
                { label: 'Download for Mac Intel', link: downloadData?.trackMacIntel?.link, platform: 'Mac Intel' }
            ].filter(v => v.link)
        },
        {
            title: 'Linux',
            icon: Icon.Linux,
            app: "Superworks",
            link: downloadData?.trackLinux?.link,
            versions: [{ label: 'Download', link: downloadData?.trackLinux?.link, platform: 'Linux' }]
        }
    ];

    const mobileItems = (prefix) => [
        {
            title: 'Chat',
            icon: Icon.UbsChat,
            link: downloadData?.[`${prefix}Chat`]?.link,
            versions: [{ label: 'Download', link: downloadData?.[`${prefix}Chat`]?.link }]
        },
        {
            title: 'HRMS',
            icon: Icon.HRMS,
            link: downloadData?.[`${prefix}HRMS`]?.link,
            versions: [{ label: 'Download', link: downloadData?.[`${prefix}HRMS`]?.link }]
        },
        {
            title: 'PMS',
            icon: Icon.PMS,
            link: downloadData?.[`${prefix}PMS`]?.link,
            versions: [{ label: 'Download', link: downloadData?.[`${prefix}PMS`]?.link }]
        }
    ];

    const hasNoData = Object.keys(downloadData)?.length === 0 || Object.values(downloadData)?.every(val => !val?.link);

    return (
        <React.Fragment>

            <div className="u_text-center u_download-main-header u_bg-thead">
                <Icon.Superworks size={70} />
                <h1 className="u_header-title">
                    <TypeWriter text={`Download Our Latest App for ${activeOS || "Windows"}`} />
                </h1>
            </div>

            {hasNoData ? (
                <div className="u_download-container u_download-no-links">
                    <NoLinks />
                    <h3>There is no update available.</h3>
                </div>
            ) : (
                <div className="u_download-container">
                    {renderDownloadSection('Superworks - Desktop App', desktopItems)}
                    {!onlyTrackApp && renderDownloadSection('Android App', mobileItems('android'))}
                    {!onlyTrackApp && renderDownloadSection('iOS App', mobileItems('ios'))}
                </div>
            )}

            {showLinuxModal &&
                <LinuxDownloadModal
                    open={showLinuxModal}
                    close={() => setShowLinuxModal(false)}
                    downloadLink={downloadData?.trackLinux?.link}
                />
            }
        </React.Fragment>
    );
};

export default UDownload;